// src/components/DirectoryChangesModal.js
//
// Shown to Power Users right after sign-in when the Procore sync changed the
// employee directory. Two steps, in order:
//   1. "Removed"  — people deactivated since this admin last pressed OK.
//      One OK button; pressing it records the acknowledgement.
//   2. "Review"   — people added or returned who still have no Asset Tracker
//      role. Each row has a role pull-down and Save. A row disappears once
//      saved; the popup closes when none are left.
// Mirrors the Labor Board's permissions-review popup so admins see the same
// thing in both apps.
import React, { useState } from 'react';
import { ackDirectoryChanges, setWorkerAtRole } from '../services/api';

export const AT_ROLE_OPTIONS = [
  { value: 'view_only',              label: 'View Only' },
  { value: 'no_access',              label: 'No Access' },
  { value: 'power_user',             label: 'Power User' },
  { value: 'general_superintendent', label: 'General Superintendent' },
  { value: 'site_supervisor',        label: 'Site Supervisor' },
  { value: 'project_manager',        label: 'Project Manager' },
  { value: 'project_administrator',  label: 'Project Administrator' },
  { value: 'project_engineer',       label: 'Project Engineer' },
  { value: 'safety_director',        label: 'Safety Director' },
  { value: 'accountant',             label: 'Accountant' },
  { value: 'foreman',                label: 'Foreman' },
  { value: 'carpenter',              label: 'Carpenter' },
  { value: 'laborer',                label: 'Laborer' },
  { value: 'driver',                 label: 'Driver' },
];

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
};
const panel = (border) => ({
  background: '#1d2a4d', border: `1px solid ${border}`, borderRadius: 16,
  padding: 20, width: '100%', maxWidth: 480, maxHeight: '80vh', display: 'flex', flexDirection: 'column',
});
const row = { display: 'flex', alignItems: 'center', gap: 8, background: '#15213f', borderRadius: 10, padding: '10px 12px' };

export default function DirectoryChangesModal({ removed = [], pending = [], onClose }) {
  const [step, setStep]       = useState(removed.length > 0 ? 'removed' : 'pending');
  const [list, setList]       = useState(pending);
  const [roles, setRoles]     = useState(() => Object.fromEntries(pending.map(p => [p.worker_id, p.at_role || 'view_only'])));
  const [saving, setSaving]   = useState(null);
  const [error, setError]     = useState('');

  const finish = () => onClose && onClose();

  const handleOk = async () => {
    try { await ackDirectoryChanges(); } catch { /* will show again next sign-in */ }
    if (list.length > 0) setStep('pending'); else finish();
  };

  const handleSave = async (workerId) => {
    setSaving(workerId); setError('');
    try {
      await setWorkerAtRole(workerId, roles[workerId] || 'view_only');
      const rest = list.filter(p => p.worker_id !== workerId);
      setList(rest);
      if (rest.length === 0) finish();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save that role. Please try again.');
    } finally { setSaving(null); }
  };

  if (step === 'removed') {
    return (
      <div style={backdrop}>
        <div style={panel('#dc2626')}>
          <h3 style={{ color: '#fca5a5', margin: '0 0 6px 0', fontSize: '1.1rem' }}>Removed from the employee list</h3>
          <p style={{ color: '#a7b1c8', fontSize: '0.82rem', margin: '0 0 14px 0' }}>
            {removed.length} {removed.length === 1 ? 'person is' : 'people are'} no longer an active employee in Procore and {removed.length === 1 ? 'has' : 'have'} been removed from both apps.
          </p>
          <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {removed.map(r => (
              <div key={r.event_id} style={{ ...row, justifyContent: 'space-between' }}>
                <span style={{ color: '#e2e8f0', fontSize: '0.88rem' }}>{r.display_name}</span>
                <span style={{ color: '#64748b', fontSize: '0.78rem', flexShrink: 0 }}>{new Date(r.occurred_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-primary" style={{ marginTop: 14 }} onClick={handleOk}>OK</button>
        </div>
      </div>
    );
  }

  if (list.length === 0) return null;

  return (
    <div style={backdrop}>
      <div style={panel('#d4a843')}>
        <h3 style={{ color: '#d4a843', margin: '0 0 6px 0', fontSize: '1.1rem' }}>Asset Tracker — Permissions Review</h3>
        <p style={{ color: '#a7b1c8', fontSize: '0.82rem', margin: '0 0 14px 0' }}>
          {list.length} new or returning employee{list.length !== 1 ? 's' : ''} need an Asset Tracker role.
        </p>
        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(w => (
            <div key={w.worker_id} style={row}>
              <span style={{ color: '#e2e8f0', fontSize: '0.88rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {w.display_name || `${w.first_name} ${w.last_name}`}
                {w.event_type === 'returned' && <span style={{ color: '#fbbf24', fontSize: '0.75rem', marginLeft: 6 }}>(returning)</span>}
              </span>
              <select
                value={roles[w.worker_id] || 'view_only'}
                onChange={e => setRoles(prev => ({ ...prev, [w.worker_id]: e.target.value }))}
                style={{ background: '#15213f', color: '#e2e8f0', border: '1px solid #33416a', borderRadius: 6, padding: '4px 6px', fontSize: '0.8rem', flexShrink: 0 }}
              >
                {AT_ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button
                onClick={() => handleSave(w.worker_id)}
                disabled={saving === w.worker_id}
                style={{ background: '#d4a843', color: '#1a2440', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', flexShrink: 0, opacity: saving === w.worker_id ? 0.5 : 1 }}
              >
                {saving === w.worker_id ? '…' : 'Save'}
              </button>
            </div>
          ))}
        </div>
        {error && <div className="alert alert-error" style={{ marginTop: 10 }}>{error}</div>}
        <button className="btn btn-ghost" style={{ marginTop: 14 }} onClick={finish}>Later</button>
      </div>
    </div>
  );
}
